import { useLanguage } from '../../i18n/LanguageContext';
import StorageImage from '../StorageImage';

interface ViewChangesDialogProps {
  proposedChanges: Record<string, unknown>;
  currentGarage: {
    name: string;
    managerName: string;
    phone: string;
    dailyCapacity: number;
    numberOfWorkers: number;
    city: string;
    area: string;
    streetAddress: string;
    description?: string | null;
    descriptionAr?: string | null;
  };
  token: string;
  onClose: () => void;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function ChangedDot() {
  return <span className="change-dot" />;
}

function FieldRow({ label, currentValue, newValue, dir }: { label: string; currentValue: string; newValue?: string; dir?: 'ltr' | 'rtl' }) {
  const changed = newValue !== undefined && String(newValue) !== String(currentValue);
  return (
    <div className="view-changes-row">
      <span className="view-changes-label">
        {changed && <ChangedDot />}
        {label}
      </span>
      <span className={`view-changes-value ${changed ? 'changed' : ''}`} dir={dir} lang={dir === 'rtl' ? 'ar' : undefined}
        style={dir ? { whiteSpace: 'pre-line' } : undefined}>
        {changed ? newValue : currentValue}
      </span>
    </div>
  );
}

export default function ViewChangesDialog({ proposedChanges, currentGarage, token, onClose }: ViewChangesDialogProps) {
  const { t } = useLanguage();
  // Spec 081a keys are added through the admin Translations page; until then show English.
  const tf = (key: string, fallback: string) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  const garageInfo = (proposedChanges.garageInfo || {}) as Record<string, unknown>;
  const workingHours = proposedChanges.workingHours as Array<Record<string, unknown>> | undefined;

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog-content dialog-wide" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="dialog-header">
          <h2>{t('profile.pending.viewChanges')}</h2>
          <button className="dialog-close" onClick={onClose}>&times;</button>
        </div>
        <div className="dialog-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <div className="view-changes-legend">
            <ChangedDot /> <span>{t('profile.changes.legend')}</span>
          </div>

          {/* Garage Info Changes */}
          {Object.keys(garageInfo).length > 0 && (
            <div className="view-changes-section">
              <h3>{t('profile.info.basicInfo')}</h3>
              <FieldRow label={t('profile.info.garageName')} currentValue={currentGarage.name} newValue={garageInfo.name as string} />
              <FieldRow label={t('profile.info.managerName')} currentValue={currentGarage.managerName} newValue={garageInfo.managerName as string} />
              <FieldRow label={t('profile.info.phone')} currentValue={currentGarage.phone} newValue={garageInfo.phone as string} />
              <FieldRow label={t('profile.info.carCapacity')} currentValue={String(currentGarage.dailyCapacity || '-')} newValue={garageInfo.dailyCapacity !== undefined ? String(garageInfo.dailyCapacity) : undefined} />

              {('city' in garageInfo || 'area' in garageInfo || 'streetAddress' in garageInfo) && (
                <>
                  <h3 style={{ marginTop: '16px' }}>{t('profile.info.location')}</h3>
                  <FieldRow label={t('profile.info.city')} currentValue={currentGarage.city || '-'} newValue={garageInfo.city as string} />
                  <FieldRow label={t('profile.info.area')} currentValue={currentGarage.area || '-'} newValue={garageInfo.area as string} />
                  <FieldRow label={t('profile.info.streetAddress')} currentValue={currentGarage.streetAddress || '-'} newValue={garageInfo.streetAddress as string} />
                </>
              )}

              {('description' in garageInfo || 'descriptionAr' in garageInfo) && (
                <div data-testid="view-changes-description">
                  <h3 style={{ marginTop: '16px' }}>{tf('profile.info.aboutGarage', 'About the garage')}</h3>
                  <FieldRow label={tf('profile.info.description', 'Description (English)')} dir="ltr"
                    currentValue={currentGarage.description || '-'}
                    newValue={'description' in garageInfo ? ((garageInfo.description as string | null) || '-') : undefined} />
                  <FieldRow label={tf('profile.info.descriptionAr', 'Description (Arabic)')} dir="rtl"
                    currentValue={currentGarage.descriptionAr || '-'}
                    newValue={'descriptionAr' in garageInfo ? ((garageInfo.descriptionAr as string | null) || '-') : undefined} />
                </div>
              )}
            </div>
          )}

          {/* Working Hours Changes */}
          {workingHours && workingHours.length > 0 && (
            <div className="view-changes-section">
              <h3>{t('profile.tab.workingHours')}</h3>
              {workingHours.map((hour) => {
                const dayIndex = (hour.dayOfWeek as number) - 1;
                const dayKey = DAY_NAMES[dayIndex];
                const dayLabel = dayKey ? t(`profile.day.${dayKey.toLowerCase()}`) : `Day ${hour.dayOfWeek}`;
                const isClosed = hour.isClosed as boolean;
                return (
                  <div key={String(hour.dayOfWeek)} className="view-changes-row">
                    <span className="view-changes-label">
                      <ChangedDot />
                      {dayLabel}
                    </span>
                    <span className="view-changes-value changed">
                      {isClosed ? t('profile.hours.closed') : `${hour.openTime} — ${hour.closeTime}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Media Changes */}
          {'media' in proposedChanges && (
            <div className="view-changes-section">
              <h3>{t('profile.tab.media')}</h3>
              {(() => {
                const media = proposedChanges.media as Record<string, unknown> | undefined;
                if (!media) return null;
                const galleryAdded = (media.galleryAdded || []) as string[];
                const galleryRemoved = (media.galleryRemoved || []) as string[];
                return (
                  <>
                    {'logoStorageKey' in media && (
                      <div className="view-changes-row">
                        <span className="view-changes-label">
                          <ChangedDot />
                          {t('profile.media.logo')}
                        </span>
                        <span className="view-changes-value changed">
                          <StorageImage
                            storageKey={String(media.logoStorageKey)}
                            token={token}
                            alt="New logo"
                            style={{ width: 80, height: 80 }}
                          />
                        </span>
                      </div>
                    )}
                    {galleryAdded.length > 0 && (
                      <div className="media-changes-section">
                        <p className="media-changes-label added">+ {t('profile.media.imagesAdded').replace('{count}', String(galleryAdded.length))}</p>
                        <div className="media-changes-images">
                          {galleryAdded.map((key, i) => (
                            <StorageImage key={i} storageKey={key} token={token} alt={`Added ${i + 1}`} style={{ width: 80, height: 80, border: '2px solid #059669', borderRadius: '8px' }} />
                          ))}
                        </div>
                      </div>
                    )}
                    {galleryRemoved.length > 0 && (
                      <div className="media-changes-section">
                        <p className="media-changes-label removed">- {t('profile.media.imagesRemoved').replace('{count}', String(galleryRemoved.length))}</p>
                        <div className="media-changes-images removed">
                          {galleryRemoved.map((key, i) => (
                            <StorageImage key={i} storageKey={key} token={token} alt={`Removed ${i + 1}`} style={{ width: 80, height: 80, border: '2px solid #ef4444', borderRadius: '8px' }} />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
